/**
 * Type-safe path builders for all application routes.
 *
 * Replaces ROUTE_PATHS constants and buildRoute helpers with functions
 * that produce concrete URL strings. No more string concatenation in components.
 *
 * Usage:
 *   import { paths } from '@/app/routing';
 *   navigate(paths.solo.dashboard());
 *   navigate(paths.shop.appointments('my-shop'));
 *   navigate(paths.client.reschedule('appointment-123'));
 */

export const paths = {
  // Public routes
  home: () => '/' as const,
  // Canonical public discovery and booking routes
  // Uses /discover and /shop (singular) to avoid collision with /shops/:slug workspace routes
  discover: () => '/discover' as const,
  shopDetails: (shopId: string) => `/shop/${shopId}` as const,
  providerBookingFlow: (shopId: string, providerId: string) =>
    `/shop/${shopId}/providers/${providerId}/book` as const,
  /** @deprecated Use paths.discover() */
  shops: () => '/discover' as const,
  /** @deprecated Use paths.discover() */
  barbershops: () => '/discover' as const,
  /** @deprecated Use paths.shopDetails() */
  barbershopDetails: (shopId: string) => `/shop/${shopId}` as const,
  /** @deprecated Use paths.providerBookingFlow() */
  barberBooking: (shopId: string, providerId: string) =>
    `/shop/${shopId}/providers/${providerId}/book` as const,
  bookingConfirmation: () => '/booking/confirmation' as const,
  providerLanding: () => '/?view=providers' as const,
  providerRegister: () => '/provider/register' as const,
  providerBooking: (providerId: string) => `/providers/${providerId}` as const,
  subscriptionSuccess: () => '/subscription/success' as const,
  subscriptionCheckout: () => '/subscription/checkout' as const,
  submitReview: () => '/reviews/submit' as const,
  emergencyOffer: (appointmentId: string, token: string) =>
    `/appointments/${appointmentId}/emergency-offer?token=${token}` as const,
  featureDetail: (slug: string) => `/features/${slug}` as const,

  // Legal & Support
  terms: () => '/terms' as const,
  privacy: () => '/privacy' as const,
  faq: () => '/faq' as const,
  contact: () => '/contact' as const,
  settings: () => '/settings' as const,

  // Auth routes
  auth: {
    callback: () => '/auth/callback' as const,
    postLogin: () => '/post-login' as const,
    unauthorized: () => '/unauthorized' as const,
  },

  // Onboarding routes
  onboarding: {
    roleSelect: () => '/onboarding/role-select' as const,
    soloSetup: () => '/onboarding/solo-setup' as const,
    shopSetup: () => '/onboarding/shop-setup' as const,
  },

  // Solo portal routes (independent providers)
  solo: {
    root: () => '/solo' as const,
    dashboard: () => '/solo/dashboard' as const,
    appointments: () => '/solo/appointments' as const,
    schedule: () => '/solo/schedule' as const,
    scheduleWorkingHours: () => '/solo/schedule/working-hours' as const,
    scheduleTimeOff: () => '/solo/schedule/time-off' as const,
    clients: () => '/solo/clients' as const,
    clientDetail: (clientId: string) => `/solo/clients/${clientId}` as const,
    services: () => '/solo/services' as const,
    reviews: () => '/solo/reviews' as const,
    earnings: () => '/solo/earnings' as const,
    settings: () => '/solo/settings' as const,
    policies: () => '/solo/policies' as const,
    payments: () => '/solo/payments' as const,
    billing: () => '/solo/billing' as const,
  },

  // Shop portal routes (multi-tenant)
  shop: {
    root: (slug: string) => `/shops/${slug}` as const,
    dashboard: (slug: string) => `/shops/${slug}/dashboard` as const,
    appointments: (slug: string) => `/shops/${slug}/appointments` as const,
    schedule: (slug: string) => `/shops/${slug}/schedule` as const,
    scheduleWorkingHours: (slug: string) => `/shops/${slug}/schedule/working-hours` as const,
    scheduleTimeOff: (slug: string) => `/shops/${slug}/schedule/time-off` as const,
    schedulePendingApprovals: (slug: string) => `/shops/${slug}/schedule/time-off/pending` as const,
    clients: (slug: string) => `/shops/${slug}/clients` as const,
    clientDetail: (slug: string, clientId: string) => `/shops/${slug}/clients/${clientId}` as const,
    services: (slug: string) => `/shops/${slug}/services` as const,
    reviews: (slug: string) => `/shops/${slug}/reviews` as const,
    earnings: (slug: string) => `/shops/${slug}/earnings` as const,
    staff: (slug: string) => `/shops/${slug}/staff` as const,
    settings: (slug: string) => `/shops/${slug}/settings` as const,
    profile: (slug: string) => `/shops/${slug}/profile` as const,
    policies: (slug: string) => `/shops/${slug}/policies` as const,
    payments: (slug: string) => `/shops/${slug}/payments` as const,
    billing: (slug: string) => `/shops/${slug}/billing` as const,
    metrics: (slug: string) => `/shops/${slug}/metrics` as const,
    revenue: (slug: string) => `/shops/${slug}/revenue` as const,
    login: (shopId: string) => `/shops/${shopId}/login` as const,
  },

  // Client routes
  client: {
    root: () => '/client' as const,
    dashboard: () => '/client/dashboard' as const,
    reschedule: (appointmentId: string) =>
      `/client/appointments/${appointmentId}/reschedule` as const,
    profile: () => '/client/profile' as const,
    bookings: () => '/client/bookings' as const,
  },

  // Provider routes (transitional onboarding/auth paths)
  provider: {
    selectShop: () => '/provider/select-shop' as const,
    invitationAccept: () => '/provider/invitation/accept' as const,
    independentSetup: () => '/provider/independent-setup' as const,
    staffOnboarding: (params?: { shopId?: string }) =>
      params?.shopId
        ? `/provider/staff-onboarding?shopId=${encodeURIComponent(params.shopId)}`
        : '/provider/staff-onboarding',
    setup: {
      root: () => '/provider/setup' as const,
      continueLater: () => '/provider/setup/continue-later' as const,
    },
  },

  // Stripe routes
  stripe: {
    connectReturn: () => '/stripe/return' as const,
    connectRefresh: () => '/stripe/refresh' as const,
  },

  // Error routes
  error: {
    notFound: () => '/404' as const,
  },
} as const;
